import type { BalanceMatrixRow, LeaveCreditsLeaveType } from '@lms/shared-types'
import { flags } from '../../lib/feature-flags'
import { ALL_LEAVE_TYPES } from '../../lib/leave-type-palette'

/**
 * Shared cell/column logic for the wide balance-and-entitlement roster.
 *
 * The on-screen table (`MatrixRoster`) and the CSV export (`balances-csv`) both
 * derive their numbers from here, so the file you download is guaranteed to
 * match — cell for cell — what the page shows. Keep display logic in this one
 * place; a copy in the export would silently drift the day the roster rules
 * change (accrual-stage annual, unpaid-taken, gender gates).
 */

export type RosterMetric = 'remaining' | 'entitled'

// Leave-type columns to show in the matrix. Excludes `marriage_family_care`:
// it is the retired 2026 combined "Marriage & Family Care" quota, kept in
// `ALL_LEAVE_TYPES` for history only (never applyable, no annual reset), so it
// has no place in the entitlement / balance roster columns.
const DISPLAY_LEAVE_TYPES = ALL_LEAVE_TYPES.filter(
  (lt) => lt !== 'marriage_family_care',
)

// The leave-type columns for a given view. Unpaid has no entitlement, so the
// Entitlement view (`entitled`) omits it entirely; the Leave Remaining Balance
// view (`remaining`) keeps it, showing days taken ("UNP · taken"). Both the
// header and the body derive their columns from here so they stay in lockstep.
export function columnLeaveTypesFor(metric: RosterMetric): typeof DISPLAY_LEAVE_TYPES {
  return metric === 'entitled'
    ? DISPLAY_LEAVE_TYPES.filter((lt) => lt !== 'unpaid')
    : DISPLAY_LEAVE_TYPES
}

export function fmtNum(n: number): string {
  if (!Number.isFinite(n)) return '—'
  return n % 1 === 0 ? String(n) : n.toFixed(1)
}

/** The computed state of one (employee, leave-type) cell — the single source
 *  the table row and the CSV export both read from. */
export interface MatrixCellComputed {
  /** Gender applicability. A non-applicable cell renders as "—" everywhere. */
  applicable: boolean
  /** The number shown in the cell for this metric (0 when not applicable). */
  value: number
  /** Remaining-view unpaid usage > 0 — flagged red on screen. */
  unpaidTaken: boolean
  /** Remaining-view accrual-stage annual overdrawn (< 0) — flagged red. */
  accrualNegative: boolean
}

export function computeMatrixCell(
  row: BalanceMatrixRow,
  lt: LeaveCreditsLeaveType,
  metric: RosterMetric,
): MatrixCellComputed {
  const cell = row.cells[lt]
  const applicable = cell?.eligible ?? false
  // Accrual-stage annual: a first-year employee's annual remaining is measured
  // against what they've accrued so far, not the full yearly slice. Gated by the
  // flag; when ON we recompute remaining from `accrued` and let it go negative.
  const isAccrualAnnual =
    flags.negativeAccrualBalance &&
    lt === 'annual' &&
    cell != null &&
    cell.accrued != null &&
    cell.accrued < cell.entitled
  const remainingValue =
    cell == null
      ? 0
      : isAccrualAnnual
        ? (cell.accrued as number) - cell.used - cell.pending
        : cell.remaining
  // 'entitled' → the year's entitlement (unpaid is 0). 'remaining' → bookable
  // balance, except unpaid which shows days *taken* (used).
  const value = cell
    ? metric === 'entitled'
      ? cell.entitled
      : lt === 'unpaid'
        ? cell.used
        : remainingValue
    : 0
  const unpaidTaken = metric === 'remaining' && lt === 'unpaid' && applicable && value !== 0
  const accrualNegative = metric === 'remaining' && isAccrualAnnual && remainingValue < 0
  return { applicable, value, unpaidTaken, accrualNegative }
}

/** The exact string a cell shows on the page: a formatted number, or "—" when
 *  the type is not applicable for the employee. Used verbatim by the CSV export
 *  so the download mirrors the on-screen layout. */
export function matrixCellDisplay(
  row: BalanceMatrixRow,
  lt: LeaveCreditsLeaveType,
  metric: RosterMetric,
): string {
  const { applicable, value } = computeMatrixCell(row, lt, metric)
  return applicable ? fmtNum(value) : '—'
}
